import { Trans, useTranslation } from "react-i18next";
import React, { type ReactNode, useEffect, useState } from "react";
import { Alert, Badge, Card, Form, Tab, Tabs } from "react-bootstrap";
import DictionaryManagerPanel from "@features/EEG/components/customization/DictionaryManagerPanel";
import ReportSnapshotEditorPanel from "@features/EEG/components/customization/ReportSnapshotEditorPanel";
import TemplatesManagerPanel from "@features/EEG/components/customization/TemplatesManagerPanel";
import TrendEvidencePanel from "@features/EEG/components/customization/TrendGovernancePanel";
import VocabularyMappingAdminPanel from "@features/EEG/components/customization/VocabularyMappingAdminPanel";
import AsyncReportSelect from "@shared/components/AsyncReportSelect";
import useRouteBackedTabs from "@shared/hooks/useRouteBackedTabs";
import apiClient from "@shared/services/apiClient";
import ChannelCatalogPage from "@features/EEGConfig/pages/ChannelCatalogPage";
import EventCatalogPage from "@features/EEGConfig/pages/EventCatalogPage";
import TrendTemplatesPage from "@features/EEGConfig/pages/TrendTemplatesPage";
import "@features/EEG/styles/customization-workspace.css";

type CustomizationTabKey =
  | "templates"
  | "event-catalog"
  | "trend-templates"
  | "electrodes"
  | "references"
  | "non-eeg-signals"
  | "report-snapshots"
  | "dictionaries"
  | "vocabulary"
  | "trend-evidence";

const CustomizationHubPage: React.FC = () => {
  const { t } = useTranslation("features");
  const [reportId, setReportId] = useState("");
  const [canManageConfiguration, setCanManageConfiguration] = useState(false);
  useEffect(() => {
    void apiClient.get("/eeg/channels/systems/access").then((response) => {
      const payload = (response.data as any)?.data ?? response.data;
      setCanManageConfiguration(Boolean(payload?.can_manage_org_eeg_config));
    }).catch(() => setCanManageConfiguration(false));
  }, []);
  const ConfigurationPanel = ({ children }: { children: ReactNode }) => <>
    {!canManageConfiguration && <Alert variant="info" className="mb-2">Organization EEG configuration is read-only for your role.</Alert>}
    <fieldset disabled={!canManageConfiguration} className="border-0 p-0 m-0">{children}</fieldset>
  </>;
  const { activeTab, handleSelect } = useRouteBackedTabs<CustomizationTabKey>({
    basePath: "/eeg/customization",
    defaultTab: "templates",
    availableTabs: [
      "templates",
      "event-catalog",
      "trend-templates",
      "electrodes",
      "references",
      "non-eeg-signals",
      "report-snapshots",
      "dictionaries",
      "vocabulary",
      "trend-evidence",
    ],
    segmentToTab: {
      "trend-demo": "trend-evidence",
      // Dual-montage prefs live in EEG Viewer → Dual Compare (same API).
      // Old hub URLs fall back to the default tab.
      "dual-montage": "templates",
    },
  });

  return (
    <div className="container py-3 eeg-customization-page app-ui-surface">
      <div className="eeg-customization-page__masthead mb-3">
        <div className="eeg-customization-page__title">
          <h2 className="app-ui-surface__heading">
            {t("eegCustomization.title")}
          </h2>
          <p className="app-ui-surface__description">
            {t("eegCustomization.description")}
          </p>
        </div>
        <div className="eeg-customization-page__actions">
          <Badge bg="primary" className="eeg-customization-page__badge">
            {t("eegCustomization.clinicalOps")}
          </Badge>
          <a
            className="btn btn-outline-primary btn-sm"
            href="/data/import-export"
          >
            {t("eegCustomization.importExport")}
          </a>
        </div>
      </div>

      <Tabs
        id="eeg-customization-modules"
        activeKey={activeTab}
        onSelect={handleSelect}
        className="mb-3 app-ui-tabs"
        mountOnEnter
      >
        <Tab eventKey="templates" title={t("eegCustomization.tabs.templates")}>
          <ConfigurationPanel><TemplatesManagerPanel /></ConfigurationPanel>
        </Tab>
        <Tab
          eventKey="event-catalog"
          title={t("eegCustomization.tabs.eventCatalog")}
        >
          <ConfigurationPanel><EventCatalogPage /></ConfigurationPanel>
        </Tab>
        <Tab
          eventKey="trend-templates"
          title={t("eegCustomization.tabs.trendTemplates")}
        >
          <ConfigurationPanel><TrendTemplatesPage /></ConfigurationPanel>
        </Tab>
        <Tab
          eventKey="electrodes"
          title={t("eegCustomization.tabs.electrodes")}
        >
          <ChannelCatalogPage mode="electrodes" />
        </Tab>
        <Tab
          eventKey="references"
          title={t("eegCustomization.tabs.references")}
        >
          <ChannelCatalogPage mode="references" />
        </Tab>
        <Tab
          eventKey="non-eeg-signals"
          title={t("eegCustomization.tabs.nonEegSignals")}
        >
          <ChannelCatalogPage mode="non-eeg" />
        </Tab>
        <Tab
          eventKey="report-snapshots"
          title={t("eegCustomization.tabs.snapshots")}
        >
          <Card className="mb-3">
            <Card.Body>
              <Form.Group>
                <Form.Label>
                  <Trans ns="features" i18nKey="ui.copy.ee45c30326b7" />
                  {reportId && (
                    <span className="ms-2 text-muted small font-monospace">
                      <Trans ns="features" i18nKey="ui.copy.04aa8e2f6ea1" />
                      {reportId.slice(-8).toUpperCase()}
                    </span>
                  )}
                </Form.Label>
                <AsyncReportSelect value={reportId} onChange={setReportId} />
              </Form.Group>
            </Card.Body>
          </Card>
          <ReportSnapshotEditorPanel reportId={reportId} />
        </Tab>
        <Tab
          eventKey="dictionaries"
          title={t("eegCustomization.tabs.dictionaries")}
        >
          <ConfigurationPanel><DictionaryManagerPanel /></ConfigurationPanel>
        </Tab>
        <Tab
          eventKey="vocabulary"
          title={t("eegCustomization.tabs.vocabulary")}
        >
          <VocabularyMappingAdminPanel />
        </Tab>
        <Tab
          eventKey="trend-evidence"
          title={t("eegCustomization.tabs.trendEvidence")}
        >
          <ConfigurationPanel>
            <TrendEvidencePanel />
          </ConfigurationPanel>
        </Tab>
      </Tabs>
    </div>
  );
};

export default CustomizationHubPage;
